import { useMemo } from "react";
import { FormattedDataPoint } from "./TimeSeriesChart.types";
import { bisector } from "d3-array";
import { format, differenceInDays } from "date-fns";
import { NumberValue } from "d3-scale";

export const defaultMargin = { top: 0, right: 0, bottom: 0, left: 0 };

export const bisectDate = bisector<FormattedDataPoint, Date>(
  (d: any) => d.date
).left;

export const useTickConfig = (
  formattedData: FormattedDataPoint[],
  xScale: any
) => {
  return useMemo(() => {
    if (formattedData.length <= 1) {
      return {
        tickFormat: (value: Date | NumberValue) => {
          const date = new Date(+value);
          return format(date, "MMM d");
        },
        tickValues: formattedData.map((d) => d.date),
      };
    }

    // Get approximately 8 ticks
    const tickCount = Math.min(8, formattedData.length);
    const step = Math.ceil(formattedData.length / tickCount);

    const ticks: Date[] = [];
    for (let i = 0; i < formattedData.length; i += step) {
      ticks.push(formattedData[i].date);
    }

    // Calculate date span to determine format
    const [startDate, endDate] = xScale.domain();
    const daySpan = differenceInDays(endDate, startDate);

    // Format ticks based on date range
    let tickFormat;
    if (daySpan <= 31) {
      tickFormat = (value: Date | NumberValue) => {
        const date = new Date(+value);
        const i = ticks.findIndex((t) => t.getTime() === date.getTime());
        const prev = i > 0 ? ticks[i - 1] : null;
        const monthChanged = !prev || date.getMonth() !== prev.getMonth();
        return monthChanged ? format(date, "d MMM") : format(date, "d");
      };
    } else if (daySpan <= 730) {
      tickFormat = (value: Date | NumberValue) => {
        const date = new Date(+value);
        const i = ticks.findIndex((t) => t.getTime() === date.getTime());
        const prev = i > 0 ? ticks[i - 1] : null;
        const yearChanged = !prev || date.getFullYear() !== prev.getFullYear();
        return yearChanged
          ? `${format(date, "MMM")[0]} '${date.getFullYear() % 100}`
          : format(date, "MMM")[0];
      };
    } else {
      tickFormat = (value: Date | NumberValue) => {
        const date = new Date(+value);
        return format(date, "yyyy");
      };
    }

    return { tickFormat, tickValues: ticks };
  }, [formattedData, xScale]);
};
